import { Button } from '@studio/design-system/button';
import { Icon } from '@studio/design-system/icon';
import { DeviceRegistryInterfaceSectionType } from '@tryiris-ai/common.protojs/api/v1/device_settings';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import { ControlSections } from './sections.jsx';
import type { Props } from './types.js';
import { handleRestoreDefaults, useWiring } from './wiring.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { getTransitoryDevice } from '#~/device/transitory.js';

/** `<Settings>` component. */
export const Settings = ({ className, deviceId, drawer, ...props }: Props): ReactElement => {
  const { agent, canRestoreDefaults, defaultsMap, nonDefaultImageSettingsMap } =
    useWiring(deviceId);
  const { track } = useAnalytics();
  const isTransitory = !!getTransitoryDevice(deviceId);

  return (
    <div className={twMerge('mb-[50px]', className)} {...props}>
      <div className='flex border-b border-base-100 px-12 py-16'>
        <Button
          className={twMerge('px-8', drawer.value && 'border-primary text-light')}
          disabled={isTransitory}
          onClick={(): void => {
            drawer(!drawer.value);
            track(EVENT_NAMES.CLICK_IMAGE_SETTING_PRESETS, EVENT_CATEGORIES.IMAGE_TRAY, {
              deviceId
            });
          }}
          variant='tertiary'
        >
          <Icon icon='preset-image' />
          <span className='ml-[-4px]'>Presets</span>
        </Button>

        <div className='flex-1' />
        <Button
          className='px-8'
          disabled={!canRestoreDefaults}
          onClick={(): void => {
            handleRestoreDefaults(agent, deviceId, nonDefaultImageSettingsMap, defaultsMap);
            // Track the reset all image settings event
            track(EVENT_NAMES.RESET_ALL_IMAGE_SETTINGS, EVENT_CATEGORIES.IMAGE_TRAY, {
              deviceId
            });
          }}
          variant='tertiary'
        >
          <Icon icon='reset' />
          <span className='ml-[-4px]'>Reset All</span>
        </Button>
      </div>
      <ControlSections
        deviceId={deviceId}
        sectionType={DeviceRegistryInterfaceSectionType.IMAGE_SETTINGS_TRAY}
      />

      {/* TODO: check if we still would like this? */}
      {/* <DynamicDeviceSettingsControls
        deviceId={deviceId}
        majorToMinorCategoryMapping={settings}
      /> */}
    </div>
  );
};
